export const FRONTEND_BASE_URL = () => process.env.REACT_APP_FRONTEND_URL;
export const API_BASE_URL = () => process.env.REACT_APP_BASE_URL + '/api';
export const PHOTOS_BASE_URL = () => process.env.REACT_APP_BASE_URL;

// Callback for handling unauthorized (401) responses
let onUnauthorizedCallback = null;

// Register callback to be called when 401 Unauthorized response is received
export const setUnauthorizedCallback = (callback) => {
    onUnauthorizedCallback = callback;
};

// Helper function to get auth headers
const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
};

// Helper function to handle API responses
const handleResponse = async (response) => {
    if (!response.ok) {
        // Handle 401 Unauthorized response
        if (response.status === 401) {
            // Call the registered callback to trigger logout and redirect
            if (onUnauthorizedCallback) {
                onUnauthorizedCallback();
            }
        }

        const contentType = response.headers.get('content-type');
        let errorMessage = 'API request failed';

        if (contentType && contentType.includes('application/json')) {
            try {
                const errorData = await response.json();
                errorMessage = errorData.message || errorData.title || JSON.stringify(errorData);
            } catch {
                errorMessage = await response.text() || errorMessage;
            }
        } else {
            errorMessage = await response.text() || errorMessage;
        }

        throw new Error(errorMessage);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        return response.json();
    }
    return response.text();
};

// Auth API
export const authApi = {
    login: async (email, password, captchaToken) => {
        const response = await fetch(`${API_BASE_URL()}/Auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, captchaToken })
        });
        return handleResponse(response);
    },

    forgotPassword: async (email, captchaToken) => {
        const response = await fetch(`${API_BASE_URL()}/auth/forgot-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, captchaToken })
        });
        return handleResponse(response);
    }
};

// Dashboard API
export const dashboardApi = {
    getStats: async (corporateId = null) => {
        const url = corporateId
            ? `${API_BASE_URL()}/Dashboard/stats?corporateId=${corporateId}`
            : `${API_BASE_URL()}/Dashboard/stats`;
        const response = await fetch(url, {
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    }
};

// Corporates API
export const corporatesApi = {
    getAll: async () => {
        const response = await fetch(`${API_BASE_URL()}/Corporates`, {
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_BASE_URL()}/Corporates/${id}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    create: async (corporate) => {
        const response = await fetch(`${API_BASE_URL()}/Corporates`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(corporate)
        });
        return handleResponse(response);
    },

    update: async (id, corporate) => {
        const response = await fetch(`${API_BASE_URL()}/Corporates/update/${id}`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(corporate)
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_BASE_URL()}/Corporates/delete/${id}`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    }
};

// Locations API
export const locationsApi = {
    getAll: async (corporateId = null) => {
        const url = corporateId
            ? `${API_BASE_URL()}/Locations?corporateId=${corporateId}`
            : `${API_BASE_URL()}/Locations`;
        const response = await fetch(url, {
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    getByQrCode: async (qrCode) => {
        const response = await fetch(`${API_BASE_URL()}/Locations/qr/${qrCode}`, {
            headers: { 'Content-Type': 'application/json' }
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_BASE_URL()}/Locations/${id}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    create: async (location) => {
        const response = await fetch(`${API_BASE_URL()}/Locations`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(location)
        });
        return handleResponse(response);
    },

    update: async (id, location) => {
        const response = await fetch(`${API_BASE_URL()}/Locations/update/${id}`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(location)
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_BASE_URL()}/Locations/delete/${id}`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    }
};

// Users API
export const usersApi = {
    getAll: async (corporateId = null) => {
        const url = corporateId
            ? `${API_BASE_URL()}/Users?corporateId=${corporateId}`
            : `${API_BASE_URL()}/Users`;
        const response = await fetch(url, {
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_BASE_URL()}/Users/${id}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    create: async (user) => {
        const response = await fetch(`${API_BASE_URL()}/Users`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(user)
        });
        return handleResponse(response);
    },

    update: async (id, user) => {
        const response = await fetch(`${API_BASE_URL()}/Users/update/${id}`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(user)
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_BASE_URL()}/Users/delete/${id}`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    changePassword: async (currentPassword, newPassword) => {
        const response = await fetch(`${API_BASE_URL()}/users/change-password`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ currentPassword, newPassword })
        });
        return handleResponse(response);
    }
};

// Forms API
export const formsApi = {
    getSubmissions: async (page = 1, pageSize = 10, corporateId = null, locationId = null, formTemplateId = null) => {
        let url = `${API_BASE_URL()}/Forms/submissions?page=${page}&pageSize=${pageSize}`;
        if (corporateId) url += `&corporateId=${corporateId}`;
        if (locationId) url += `&locationId=${locationId}`;
        if (formTemplateId) url += `&formTemplateId=${formTemplateId}`;

        const response = await fetch(url, {
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    getSubmissionDetail: async (id) => {
        const response = await fetch(`${API_BASE_URL()}/Forms/submissions/${id}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    getSubmissionReport: async (corporateId = null, locationId = null, formTemplateId = null, startDate = null, endDate = null) => {
        let url = `${API_BASE_URL()}/Forms/submissionreport`;
        const params = [];
        if (corporateId) params.push(`corporateId=${corporateId}`);
        if (locationId) params.push(`locationId=${locationId}`);
        if (formTemplateId) params.push(`formTemplateId=${formTemplateId}`);
        if (startDate) params.push(`startDate=${startDate}`);
        if (endDate) params.push(`endDate=${endDate}`);
        if (params.length > 0) url += `?${params.join('&')}`;

        const response = await fetch(url, {
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    getTemplates: async (corporateId = null, locationId = null) => {
        let url = `${API_BASE_URL()}/Forms/templates`;
        const params = [];
        if (corporateId) params.push(`corporateId=${corporateId}`);
        if (locationId) params.push(`locationId=${locationId}`);
        if (params.length > 0) url += `?${params.join('&')}`;

        const response = await fetch(url, {
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    getTemplateById: async (id) => {
        const response = await fetch(`${API_BASE_URL()}/Forms/templates/${id}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    createTemplate: async (template) => {
        const response = await fetch(`${API_BASE_URL()}/Forms/templates`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(template)
        });
        return handleResponse(response);
    },

    updateTemplate: async (id, template) => {
        const response = await fetch(`${API_BASE_URL()}/Forms/templates/update/${id}`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(template)
        });
        return handleResponse(response);
    },

    deleteTemplate: async (id) => {
        const response = await fetch(`${API_BASE_URL()}/Forms/templates/delete/${id}`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    submitForm: async (submission) => {
        const response = await fetch(`${API_BASE_URL()}/Forms/submit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(submission)
        });
        return handleResponse(response);
    },

    getTemplatesPublic: async (corporateId) => {
        const response = await fetch(`${API_BASE_URL()}/Forms/templates?corporateId=${corporateId}`, {
            headers: { 'Content-Type': 'application/json' }
        });
        return handleResponse(response);
    }
};

// SMS API
export const smsApi = {
    // Returns { userIds: number[] } extracted from List<SmsUserDTO>
    getSmsUsers: async (corporateId) => {
        const response = await fetch(`${API_BASE_URL()}/Sms/users?corporateId=${corporateId}`, {
            headers: getAuthHeaders()
        });
        const data = await handleResponse(response);
        return { userIds: (data || []).map((item) => item.userId) };
    },

    // Sends List<SmsUserInputDTO> → [{ userId }, ...]
    updateSmsUsers: async (corporateId, userIds) => {
        const body = userIds.map((id) => ({ userId: id }));
        const response = await fetch(`${API_BASE_URL()}/Sms/users?corporateId=${corporateId}`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(body)
        });
        return handleResponse(response);
    }
};

export default {
    auth: authApi,
    dashboard: dashboardApi,
    corporates: corporatesApi,
    locations: locationsApi,
    users: usersApi,
    forms: formsApi,
    sms: smsApi
};
